let refreshingPromise = null;
let lastRefreshAt = 0;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return false;

  const res = await fetch("/account/refresh/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: refreshToken })
  });

  if (!res.ok) return false;

  const data = await res.json().catch(() => null);
  if (!data?.access) return false;

  localStorage.setItem("authToken", data.access);
  if (data?.refresh) localStorage.setItem("refreshToken", data.refresh);

  lastRefreshAt = Date.now();
  return true;
}

function shouldSkipRefresh(path) {
  return (
    typeof path === "string" &&
    (
      path.startsWith("/account/refresh/") ||
      path.startsWith("/account/login/") ||
      path.startsWith("/account/verify/")
    )
  );
}

async function ensureRefreshed() {
  const now = Date.now();

  // 방금 갱신했으면 추가 refresh 억제 (낭비/연속 갱신 방지)
  if (now - lastRefreshAt < 1500) return true;

  if (!refreshingPromise) {
    refreshingPromise = (async () => {
      try {
        return await refreshAccessToken();
      } finally {
        refreshingPromise = null;
      }
    })();
  }

  return await refreshingPromise;
}

async function parseResponse(res) {
  if (res.status === 204) {
    return { ok: true, data: null, status: 204 };
  }

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const text = await res.text();

  const looksHtml =
    ct.includes("text/html") ||
    /^\s*<!doctype html/i.test(text) ||
    /^\s*<html[\s>]/i.test(text);

  if (looksHtml) {
    return {
      ok: false,
      status: res.status,
      error: new Error(`서버 데이터 오류. (HTTP ${res.status})`)
    };
  }

  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })()
    : null;

  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && (data.error || data.detail)) ||
      (typeof data === "string" && data) ||
      `HTTP ${res.status}`;

    return {
      ok: false,
      status: res.status,
      error: new Error(String(msg))
    };
  }

  return { ok: true, status: res.status, data };
}

export async function apiFetch(path, options = {}) {
  const {
    method = "GET",
    headers = {},
    body,
    signal,
    cache = "no-store"
  } = options;

  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const makeHeaders = () => {
    const token = localStorage.getItem("authToken");
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body && !isFormData ? { "Content-Type": "application/json" } : {}),
      ...headers
    };
  };

  const doFetch = async () => {
    return fetch(path, {
      method,
      cache,
      signal,
      headers: makeHeaders(),
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined
    });
  };

  // 1) 첫 요청
  let res = await doFetch();
  let parsed = await parseResponse(res);

  // 2) 401이면 refresh(락+쿨다운) 후 1회 재시도
  if (!parsed.ok && parsed.status === 401 && !shouldSkipRefresh(path)) {
    // 방금 refresh 했을 가능성이 높으면 refresh 말고 재시도부터
    if (Date.now() - lastRefreshAt < 5000) {
      res = await doFetch();
      parsed = await parseResponse(res);
      if (parsed.ok) return parsed.data;
    }

    const refreshed = await ensureRefreshed();

    if (!refreshed) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
      throw new Error("세션이 만료되었습니다. 다시 로그인해 주세요.");
    }

    res = await doFetch();
    parsed = await parseResponse(res);
  }

  if (!parsed.ok) throw parsed.error;
  return parsed.data;
}
